/**
 * Chat Loading States
 *
 * Loading indicators and skeleton states for the AI Chat interface.
 */

import React from 'react';
import { Box, Skeleton, CircularProgress, Typography, keyframes } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';

/**
 * Typing Indicator - Animated dots showing AI is responding
 */
export const TypingIndicator: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Box className={classes.typingContainer}>
      <Box className={classes.typingBubble}>
        <SmartToyIcon className={classes.aiIcon} />
        <Box className={classes.typingDots}>
          <span className={classes.dot} style={{ animationDelay: '0ms' }} />
          <span className={classes.dot} style={{ animationDelay: '150ms' }} />
          <span className={classes.dot} style={{ animationDelay: '300ms' }} />
        </Box>
        <Typography variant="caption" className={classes.typingText}>
          {t('aiChatTyping', 'SAIA is typing...')}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Message Skeleton - Placeholder for loading messages
 */
interface MessageSkeletonProps {
  isUser?: boolean;
}

export const MessageSkeleton: React.FC<MessageSkeletonProps> = ({ isUser = false }) => {
  const { classes, cx } = useStyles();

  return (
    <Box className={cx(classes.skeletonContainer, isUser && 'user')}>
      <Box className={classes.skeletonBubble}>
        <Skeleton variant="text" width="80%" height={20} />
        <Skeleton variant="text" width="60%" height={20} />
        <Skeleton variant="text" width="40%" height={16} />
      </Box>
    </Box>
  );
};

/**
 * Chat History Loading - Full skeleton for initial chat load
 */
export const ChatHistoryLoading: React.FC = () => {
  const { classes } = useStyles();

  return (
    <Box className={classes.historyLoading}>
      <MessageSkeleton />
      <MessageSkeleton isUser />
      <MessageSkeleton />
      <MessageSkeleton isUser />
      <MessageSkeleton />
    </Box>
  );
};

/**
 * Processing Indicator - Shows when AI is processing a complex request
 */
interface ProcessingIndicatorProps {
  message?: string;
}

export const ProcessingIndicator: React.FC<ProcessingIndicatorProps> = ({ message }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Box className={classes.processingContainer}>
      <CircularProgress size={24} className={classes.processingSpinner} />
      <Typography variant="body2" className={classes.processingText}>
        {message || t('aiChatProcessing', 'Processing your request...')}
      </Typography>
    </Box>
  );
};

/**
 * Card Loading Skeleton
 */
export const CardSkeleton: React.FC = () => {
  const { classes } = useStyles();

  return (
    <Box className={classes.cardSkeleton}>
      <Skeleton variant="rectangular" height={40} width="100%" />
      <Box mt={1}>
        <Skeleton variant="text" width="70%" height={24} />
        <Skeleton variant="text" width="50%" height={20} />
        <Skeleton variant="text" width="90%" height={20} />
      </Box>
      <Box mt={2} display="flex" gap={1}>
        <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1 }} />
      </Box>
    </Box>
  );
};

/**
 * Initial Loading Screen
 */
export const InitialLoading: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Box className={classes.initialLoading}>
      <Box className={classes.logoContainer}>
        <SmartToyIcon className={classes.loadingLogo} />
      </Box>
      <Typography variant="h6" className={classes.loadingTitle}>
        {t('aiChatTitle', 'SAIA')}
      </Typography>
      <CircularProgress size={28} className={classes.initialSpinner} />
      <Typography variant="body2" color="textSecondary">
        {t('aiChatLoading', 'Loading...')}
      </Typography>
    </Box>
  );
};

const bounce = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-4px);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const useStyles = makeStyles()((theme) => ({
  // Typing Indicator
  typingContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    padding: theme.spacing(1),
  },
  typingBubble: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.palette.grey[100],
    borderRadius: 16,
    borderTopLeftRadius: 4,
  },
  aiIcon: {
    fontSize: '1.25rem',
    color: theme.palette.primary.main,
  },
  typingDots: {
    display: 'flex',
    gap: 3,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: theme.palette.grey[500],
    animation: `${bounce} 1s infinite`,
  },
  typingText: {
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(0.5),
  },

  // Message Skeleton
  skeletonContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    padding: theme.spacing(1),
    '&.user': {
      justifyContent: 'flex-end',
    },
  },
  skeletonBubble: {
    maxWidth: '70%',
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[100],
    borderRadius: 12,
  },

  // History Loading
  historyLoading: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
  },

  // Processing Indicator
  processingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main + '08',
    borderRadius: 8,
    margin: theme.spacing(1),
  },
  processingSpinner: {
    color: theme.palette.primary.main,
  },
  processingText: {
    color: theme.palette.text.secondary,
  },

  // Card Skeleton
  cardSkeleton: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
  },

  // Initial Loading
  initialLoading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
  },
  logoContainer: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main + '15',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: `${pulse} 2s infinite`,
  },
  loadingLogo: {
    fontSize: 32,
    color: theme.palette.primary.main,
  },
  loadingTitle: {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  initialSpinner: {
    color: theme.palette.primary.main,
  },
}));

export default TypingIndicator;

